// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/jobs/model/fert_rx.py.
// Change both and update the parity fixtures in shared/vectors/rx_presets.json.
// Why: the public API builds Rx on the server (ADR 0003 and ADR 0009 amendments).
// CI runs the same fixtures in frontend/src/lib/rx.vectors.test.ts and
// backend/worker_tests/test_rx_vectors.py. A change to one side alone fails CI.
// ============================================================================
// So this is the lime rec's accuracy ceiling for as long as the labs we receive do not run the test
// a procurement question, not an engineering one.
import type { ManifestLayer } from "../types/contract";
import {
  LimeTargetOutOfRangeError,
  targetInTristateRange,
  tristateTons,
  type BphFlag,
} from "./limeBuffer";
import type { Replacement } from "./replacement";

export const TRI_STATE = {
  P_CRIT_PPM: 20,
  P_LBS_P2O5_PER_PPM: 15,
  K_CRIT_PPM_LOW_CEC: 100, // CEC ≤ 5 (sandy)
  K_CRIT_PPM_HIGH_CEC: 120, // CEC > 5 (the common case), and when CEC is unknown
  K_CEC_BREAK: 5,
  K_LBS_K2O_PER_PPM: 8,
  PH_TARGET: 6.5,
  // Tons of 90% CCE lime (same basis as Tri-State Table 2). No published source;
  // checked against Tri-State Table 2 on depth-trial data, see docs note.
  LIME_CEC_FACTOR: 0.32,
  LIME_MAX_T_AC: 4, // per-application cap on tons of 90% CCE lime
} as const;

// Full BCSR = balance the base-cation saturation ratios Ca:Mg and Mg:K (meq basis). Both are
// corrected by ADDING Mg where it's short (buildup only, consistent with the base engine — no
// cation removal); plus an extra K₂O nudge to reach the BCSR K-saturation minimum when the
// sufficiency K rec leaves it below.
export const BCSR = {
  CA_MG_TARGET: 6.5, // Ca:Mg meq ratio (range ~5–8)
  MG_K_TARGET: 2.0, // Mg:K meq ratio (range ~2–3)
  K_SAT_TARGET: 0.03, // K as 3% of CEC (range 2–5%)
  CA_PPM_PER_MEQ: 200, // 1 meq Ca/100g = 200 ppm
  MG_PPM_PER_MEQ: 120, // 1 meq Mg/100g = 120 ppm
  K_PPM_PER_MEQ: 390, // 1 meq K/100g = 390 ppm
  LBS_PER_PPM_8IN: 2.4, // acre-8" furrow slice ≈ 2.4M lb → 1 ppm = 2.4 lb/ac
} as const;

// Albrecht / BCSR Soil-Balance BASIS — an ALTERNATIVE to the buildup-to-critical basis for K₂O and
// Lime: build cations to a target SATURATION % of CEC instead of to a critical ppm (K) / target pH
// (lime).
export interface AlbrechtTargets {
  caPct: number; // Ca base-saturation target (%)
  mgPct: number; // Mg (%)
  kPct: number; // K (%)
  naPct: number;
}
export const DEFAULT_ALBRECHT_TARGETS: AlbrechtTargets = {
  caPct: 65,
  mgPct: 10,
  kPct: 3,
  naPct: 1,
};
export const ALBRECHT = {
  K2O_FROM_K: 1.205, // K₂O/K mass ratio (94.2/78.2)
  CACO3_EQ: 50, // CaCO₃ equivalent weight (mg/meq)
  PPM_SCALE: 10, // meq/100g × eq.wt(mg/meq) × 10 = ppm (the 100g → kg step)
} as const;

/*
 *
 * That 8 is a BUILDUP rate — it carries a buildup EFFICIENCY factor (~0.36), because moving a soil
 * test upward means over-applying: most of what you spread does not show up as extractable K in the
 * year you spread it. Running that same number backwards would credit surplus at what it would have
 * COST to build, not at what the surplus actually IS, and ~37 ppm of surplus would have erased a
 * whole 300 lb removal season.
 */
export const K_LBS_K2O_PER_PPM_CREDIT =
  BCSR.LBS_PER_PPM_8IN * ALBRECHT.K2O_FROM_K;

/* * P₂O₅/P mass ratio (141.94 / 61.94) — the oxide conversion, the P twin of `ALBRECHT.K2O_FROM_K`. */
export const P2O5_FROM_P = 2.2915;

/*
 *
 * 2.4 lb P/ac per ppm × 2.2915 ≈ 5.50 lb P₂O₅/ac per ppm Against the buildup rate of 15 that
 * implies a buildup efficiency of ~0.37 — within a point of the K pair's 0.36, which is a useful
 * independent check that the two nutrients are being treated consistently rather than each tuned by
 * hand.
 */
export const P_LBS_P2O5_PER_PPM_CREDIT = BCSR.LBS_PER_PPM_8IN * P2O5_FROM_P;

export interface RecInputs {
  ph: (number | null)[];
  p: (number | null)[];
  k: (number | null)[];
  cec: (number | null)[];
  /** Optional buffer pH (ml-bph). Finite cells use the Tri-State IN/MI table. */
  bph?: (number | null)[];
  ca?: (number | null)[]; // for cation balance
  mg?: (number | null)[];
  kFixEff?: (number | null)[];
  pMinCredit?: (number | null)[];
  // Set only when a diffusion supply map was applied (so `p`/`k` already hold the corrected
  // values).
  pRaw?: (number | null)[];
  kRaw?: (number | null)[];
  // OPTIONAL ALBRECHT / BCSR SOIL-BALANCE BASIS — when set, the K₂O and/or Lime BASE rec is
  // computed by cation-saturation targeting (albrechtK2O / albrechtLimeTons) INSTEAD of buildup
  // to-critical.
  k2oAlbrecht?: boolean;
  limeAlbrecht?: boolean;
  // ---- Rx-editor tunables. All optional; absent = the engine's documented defaults, so nothing that does
  // not open the editor changes behaviour.
  limeMinTAc?: number; // floor: min floors every non-dropped cell, and the window applies to the final rate
  limeMaxTAc?: number; // ceiling; defaults to TRI_STATE.LIME_MAX_T_AC (the per-application spreading limit)
  limeDropBelowTAc?: number; // cells whose final lime rate is below this get 0; absent = off
  albrechtTargets?: AlbrechtTargets;
  // Overrides the Tri-State default (P 20 ppm / CEC-tiered K).
  pCritCustom?: number | null;
  kCritCustom?: number | null;
  // Each is OPTIONAL and absent means "engine default", so a preset that never opens the editor
  // behaves exactly as before.
  kCritLowCec?: number | null; // ppm critical at CEC ≤ 5 (default TRI_STATE.K_CRIT_PPM_LOW_CEC)
  kCritHighCec?: number | null; // ppm critical at CEC > 5 (default TRI_STATE.K_CRIT_PPM_HIGH_CEC)
  // Absent = engine default, so a preset that never opens the editor behaves exactly as before.
  pMinLbAc?: number; // rate floor — min floors every non-dropped cell; the window applies to the final rate
  pMaxLbAc?: number; // ceiling; 0/absent = none
  pDropBelowLbAc?: number; // cells whose final P₂O₅ rate is below this get 0; absent = off
  pMaintenance?: (number | null)[]; // per-cell crop removal, folded INTO the rec (never a separate layer)
  // PER-CELL MAINTENANCE (crop removal), folded into the rec itself rather than added to a separate
  // total layer. Being INSIDE the rec is the whole point: the fixation adjustment and the min/max
  // window then both act on the number the rec actually calls for.
  kMaintenance?: (number | null)[];
  kMinLbAc?: number; // floor: min floors every non-dropped cell, and the window applies to the final rate
  kMaxLbAc?: number; // ceiling — the per-application spreading limit
  kDropBelowLbAc?: number; // cells whose final K₂O rate is below this get 0; absent = off
  // Absent = window the base, then add Genius corrections (every saved set from before the pin).
  // "final" windows the rate after those corrections. New saves set it.
  rateWindow?: "final";
  // OPTIONAL USER-ENTERED LIME TARGET pH: overrides the 6.5 default wherever the ΔpH buildup basis
  // runs — including the per-cell fallback under the Albrecht lime basis (which itself targets H%
  // not pH, so the custom target only reaches its fallback cells).
  phTargetCustom?: number | null;
  /** Stamped on a new lime Rx that used the buffer table. Absent on older saves. */
  limeBasis?: "tristate_inmi_2020";
  limeMethodCounts?: { buffer: number; water: number };
}
export interface RecOutputs {
  lime: (number | null)[]; // tons ag lime / ac
  p2o5: (number | null)[]; // lbs P2O5 / ac
  k2o: (number | null)[]; // lbs K2O / ac
  // Unwindowed, unrounded lb/ac, so a total can window base + corrections together.
  // Distinct from the soil-test `pRaw` / `kRaw` on RecInputs.
  pRaw?: (number | null)[];
  kRaw?: (number | null)[];
  /** Per cell. Null is an Albrecht Ca cell or a cell with no lime inputs. */
  limeMethod?: (("buffer" | "water") | null)[];
  /** Post-gate tons of 90% NV lime. Null on water and Albrecht cells. */
  limeTristate?: (number | null)[];
  limeFlag?: (BphFlag | null)[];
}

export interface RateWindow {
  drop?: number | null;
  min?: number | null;
  max?: number | null;
}

// One window for P, K, and lime. Dropped cells stay 0 — the floor does not raise them.
// max of 0 or absent is no ceiling. Rounding happens after this returns.
export function applyRateWindow(v: number, window: RateWindow): number {
  const drop = window.drop;
  if (drop != null && Number.isFinite(drop) && v < drop) return 0;
  const min =
    window.min != null && Number.isFinite(window.min) ? window.min : 0;
  let out = Math.max(v, min);
  const max = window.max;
  if (max != null && Number.isFinite(max) && max > 0) out = Math.min(out, max);
  return out;
}

function positiveOrZero(value: number | null | undefined): number {
  return num(value) && value! > 0 ? value! : 0;
}

function dropOrNull(value: number | null | undefined): number | null {
  return num(value) && value! >= 0 ? value! : null;
}

// The ceiling stays at least the floor, so a max typed below the min cannot invert the rate.
function pkWindow(
  minRaw: number | null | undefined,
  maxRaw: number | null | undefined,
  dropRaw: number | null | undefined,
): RateWindow {
  const min = positiveOrZero(minRaw);
  const cap = num(maxRaw) && maxRaw! > 0 ? maxRaw! : null;
  return {
    drop: dropOrNull(dropRaw),
    min,
    max: cap == null ? null : Math.max(cap, min),
  };
}

export function pRateWindow(inp: RecInputs): RateWindow {
  return pkWindow(inp.pMinLbAc, inp.pMaxLbAc, inp.pDropBelowLbAc);
}

export function kRateWindow(inp: RecInputs): RateWindow {
  return pkWindow(inp.kMinLbAc, inp.kMaxLbAc, inp.kDropBelowLbAc);
}
export interface CationOutputs {
  mg: (number | null)[]; // lbs Mg / ac to satisfy Ca:Mg + Mg:K
  k2oCorr: (number | null)[]; // extra lbs K2O / ac (beyond the base rec) to hit BCSR K saturation
}

const num = (v: number | null | undefined): v is number =>
  v != null && Number.isFinite(v);

// The trained model has no Ca or Mg map, so cation balance needs soil-test
// samples (at least four of each) plus the K and CEC maps.
export const CATION_MIN_SAMPLE_PTS = 4;
export function cationInputsAvailable(
  availProp: Set<string>,
  caSamplePts: number,
  mgSamplePts: number,
): boolean {
  return (
    caSamplePts >= CATION_MIN_SAMPLE_PTS &&
    mgSamplePts >= CATION_MIN_SAMPLE_PTS &&
    availProp.has("k") &&
    availProp.has("cec")
  );
}

const kCritPpm = (cec: number | null | undefined, inp?: RecInputs) => {
  // The CEC break is the bulletin's, not a preference — only the two LEVELS are editable.
  const low = num(inp?.kCritLowCec)
    ? inp!.kCritLowCec!
    : TRI_STATE.K_CRIT_PPM_LOW_CEC;
  const high = num(inp?.kCritHighCec)
    ? inp!.kCritHighCec!
    : TRI_STATE.K_CRIT_PPM_HIGH_CEC;
  // An UNKNOWN CEC takes the high critical, as it always has: under-recommending K on the majority soil
  // type is the worse error, and most cropland here is CEC>5.
  return num(cec) && cec <= TRI_STATE.K_CEC_BREAK ? low : high;
};

// The effective critical the sufficiency rec targets, in precedence order user-entered CUSTOM fixed
// critical > Tri-State default (P 20 ppm / CEC-tiered K).
const pCritAt = (inp: RecInputs): number =>
  num(inp.pCritCustom) ? inp.pCritCustom! : TRI_STATE.P_CRIT_PPM;
const pBuildupRaw = (inp: RecInputs): (number | null)[] => {
  const pCrit = pCritAt(inp);
  return inp.p.map((p) =>
    num(p) ? Math.max(0, pCrit - p) * TRI_STATE.P_LBS_P2O5_PER_PPM : null,
  );
};
// the lime target pH the buildup basis aims at: user custom (custom panel) > 6.5 default
const phTargetAt = (inp: RecInputs): number =>
  num(inp.phTargetCustom) && inp.phTargetCustom > 0
    ? inp.phTargetCustom
    : TRI_STATE.PH_TARGET;
const kCritAt = (inp: RecInputs, cec: number | null | undefined): number =>
  num(inp.kCritCustom) ? inp.kCritCustom! : kCritPpm(cec, inp);

// Default to the mid-range Albrecht targets; the grower can override Ca:Mg and Mg:K in the UI.
export interface CationRatios {
  caMg: number; // Ca:Mg target
  mgK: number; // Mg:K target
}
export const DEFAULT_CATION_RATIOS: CationRatios = {
  caMg: BCSR.CA_MG_TARGET,
  mgK: BCSR.MG_K_TARGET,
};

// BCSR correction per cell. Requires ca, mg, k, cec (null out otherwise — the correction
// simply won't render where its inputs are missing). `ratios` sets the Ca:Mg / Mg:K targets.
export function computeCationBalance(
  inp: RecInputs,
  ratios: CationRatios = DEFAULT_CATION_RATIOS,
): CationOutputs {
  const caMgTarget =
    num(ratios.caMg) && ratios.caMg > 0 ? ratios.caMg : BCSR.CA_MG_TARGET;
  const mgKTarget =
    num(ratios.mgK) && ratios.mgK > 0 ? ratios.mgK : BCSR.MG_K_TARGET;
  const ca = inp.ca ?? [];
  const mg = inp.mg ?? [];
  const n = Math.max(ca.length, mg.length, inp.k.length, inp.cec.length);
  const mgOut: (number | null)[] = new Array(n).fill(null);
  const k2oCorr: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const caV = ca[i];
    const mgV = mg[i];
    const kV = inp.k[i];
    const cecV = inp.cec[i];
    if (num(caV) && num(mgV) && num(kV) && num(cecV) && cecV > 0) {
      const caMeq = caV / BCSR.CA_PPM_PER_MEQ;
      const mgMeq = mgV / BCSR.MG_PPM_PER_MEQ;
      const kMeq = kV / BCSR.K_PPM_PER_MEQ;
      // Mg to bring Ca:Mg down to target (raise Mg) and Mg:K up to target (raise Mg) —
      // take the larger requirement; one Mg application satisfies both.
      const needCaMg = Math.max(0, caMeq / caMgTarget - mgMeq);
      const needMgK = Math.max(0, mgKTarget * kMeq - mgMeq);
      const mgDeficitMeq = Math.max(needCaMg, needMgK);
      mgOut[i] = Math.round(
        mgDeficitMeq * BCSR.MG_PPM_PER_MEQ * BCSR.LBS_PER_PPM_8IN,
      );
      // extra K₂O to reach the BCSR K-saturation floor, beyond what the sufficiency rec
      // already builds (the base rec takes K to kCrit; don't double-count that). Use the SAME
      // critical the base rec used (custom > CEC-tiered) else the total double-counts the band.
      const kSatTargetPpm = BCSR.K_SAT_TARGET * cecV * BCSR.K_PPM_PER_MEQ;
      const baseCrit = kCritAt(inp, cecV);
      const alreadyTo = Math.max(kV, baseCrit);
      k2oCorr[i] = Math.round(
        Math.max(0, kSatTargetPpm - alreadyTo) * TRI_STATE.K_LBS_K2O_PER_PPM,
      );
    }
  }
  return { mg: mgOut, k2oCorr };
}

export function albrechtBaseSatPct(
  caPpm: number,
  mgPpm: number,
  kPpm: number,
  cec: number,
): number {
  if (!(cec > 0)) return 0;
  const meq =
    caPpm / BCSR.CA_PPM_PER_MEQ +
    mgPpm / BCSR.MG_PPM_PER_MEQ +
    kPpm / BCSR.K_PPM_PER_MEQ;
  return (meq / cec) * 100;
}
// Returns lbs K₂O/ac (≥0) on the 8" slice the caller applies any clay K-fixation efficiency — the
// buildup/efficiency factor DOES apply.
export function albrechtK2O(kPpm: number, cec: number, kPct: number): number {
  const deficitMeq = Math.max(
    0,
    (kPct / 100) * cec - kPpm / BCSR.K_PPM_PER_MEQ,
  );
  const lbsK = deficitMeq * BCSR.K_PPM_PER_MEQ * BCSR.LBS_PER_PPM_8IN;
  return lbsK * ALBRECHT.K2O_FROM_K;
}
// Lime to neutralize exchangeable acidity down to the target H% saturation. Returns tons CaCO₃-equiv/ac
// capped at the per-application max (same cap as the buildup basis). Needs Ca+Mg+K+CEC.
/*
 *
 * A cell at or above target gets 0, never a removal.
 */
export function albrechtLimeTons(
  caPpm: number,
  cec: number,
  t: AlbrechtTargets,
): number {
  if (!(cec > 0)) return 0;
  const deficitMeq = Math.max(
    0,
    (t.caPct / 100) * cec - caPpm / BCSR.CA_PPM_PER_MEQ,
  );
  const lbsCaCO3 =
    deficitMeq * ALBRECHT.CACO3_EQ * ALBRECHT.PPM_SCALE * BCSR.LBS_PER_PPM_8IN;
  return Math.min(lbsCaCO3 / 2000, TRI_STATE.LIME_MAX_T_AC);
}

// element-wise sum treating null as absent (null + x = x; null + null = null)
export function sumArrays(
  a: (number | null)[],
  b: (number | null)[],
): (number | null)[] {
  const n = Math.max(a.length, b.length);
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const av = a[i];
    const bv = b[i];
    if (num(av) || num(bv)) out[i] = (num(av) ? av : 0) + (num(bv) ? bv : 0);
  }
  return out;
}

/*
 *
 * A minimum rate is a blanket-application decision — "no acre goes out with less than this" — not a
 * rounding rule for ground that already needs something, and it is only ever reached because a user
 * typed a number into it. `clampK` carries the identical rule, deliberately: the two controls now
 * mean the same thing on both nutrients, and the shared behaviour is pinned by tests on each side.
 */
function clampLime(tons: number, inp: RecInputs): number {
  const cap =
    num(inp.limeMaxTAc) && inp.limeMaxTAc! > 0
      ? inp.limeMaxTAc!
      : TRI_STATE.LIME_MAX_T_AC;
  const min = num(inp.limeMinTAc) && inp.limeMinTAc! > 0 ? inp.limeMinTAc! : 0;
  return applyRateWindow(tons, {
    drop: dropOrNull(inp.limeDropBelowTAc),
    min,
    max: Math.max(cap, min),
  });
}

/*
 *
 * The K twin of `clampLime`. Min floors every non-dropped cell, including one whose math is 0.
 * A drop sends everything below it to 0 and the floor does not raise those. Absent max = no ceiling
 * (K has no platform-wide spreading limit the way lime has 4 t/ac).
 */
function clampK(lb: number, inp: RecInputs): number {
  // A cell with NO K data still gets nothing: the caller's `num(k)` gate never reaches this, so the
  // floor can only ever raise a real recommendation, never invent one on ground we cannot see.
  return applyRateWindow(lb, kRateWindow(inp));
}

/* * The P twin of `clampK` — identical rule, including the blanket floor. */
function clampP(lb: number, inp: RecInputs): number {
  return applyRateWindow(lb, pRateWindow(inp));
}

function albrechtLimeCell(
  inp: RecInputs,
  ph: number | null | undefined,
  cec: number | null | undefined,
  ca: number | null | undefined,
): boolean {
  return !!(inp.limeAlbrecht && num(ca) && num(cec) && cec > 0 && num(ph));
}

function assertBufferTarget(inp: RecInputs, n: number, target: number): void {
  if (!inp.bph || targetInTristateRange(target)) return;
  for (let i = 0; i < n; i++) {
    const ph = inp.ph[i];
    const cec = inp.cec[i];
    if (num(inp.bph[i]) && !albrechtLimeCell(inp, ph, cec, inp.ca?.[i])) {
      throw new LimeTargetOutOfRangeError(target);
    }
  }
}

export function limeMethodCounts(
  methods: (("buffer" | "water") | null)[] | undefined,
): { buffer: number; water: number } {
  const counts = { buffer: 0, water: 0 };
  for (const method of methods ?? []) {
    if (method === "buffer") counts.buffer += 1;
    else if (method === "water") counts.water += 1;
  }
  return counts;
}

/** New lime saves record which cells used the buffer table. Older saves omit the fields. */
export function withLimeSoilStamp(
  soil: RecInputs,
  nutrient: "lime" | "p2o5" | "k2o",
): RecInputs {
  if (nutrient !== "lime") return soil;
  const counts = limeMethodCounts(computeRecs(soil).limeMethod);
  const next: RecInputs = { ...soil, limeMethodCounts: counts };
  if (counts.buffer > 0) next.limeBasis = "tristate_inmi_2020";
  return next;
}

export function computeRecs(inp: RecInputs): RecOutputs {
  const n = Math.max(
    inp.ph.length,
    inp.p.length,
    inp.k.length,
    inp.cec.length,
    inp.bph?.length ?? 0,
  );
  const lime: (number | null)[] = new Array(n).fill(null);
  const p2o5: (number | null)[] = new Array(n).fill(null);
  const k2o: (number | null)[] = new Array(n).fill(null);
  const pRaw: (number | null)[] = new Array(n).fill(null);
  const kRaw: (number | null)[] = new Array(n).fill(null);
  const limeMethod: (("buffer" | "water") | null)[] = new Array(n).fill(null);
  const limeTristate: (number | null)[] = new Array(n).fill(null);
  const limeFlag: (BphFlag | null)[] = new Array(n).fill(null);
  const albT = inp.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS;
  const target = phTargetAt(inp);
  assertBufferTarget(inp, n, target);
  for (let i = 0; i < n; i++) {
    const ph = inp.ph[i];
    const cec = inp.cec[i];
    const caV = inp.ca?.[i];
    const bph = inp.bph?.[i];
    // The clamp is the existing per-application window. Product tons are the spread step's job.
    let t: number | null = null;
    if (albrechtLimeCell(inp, ph, cec, caV) && num(caV) && num(cec)) {
      // Albrecht basis: raise Ca saturation to the target — Ca + CEC suffice. Unchanged when Ca is present.
      t = albrechtLimeTons(caV, cec, albT);
    } else if (num(bph)) {
      const hit = tristateTons(bph, target);
      // The table is already tons of 90% lime. Store it as-is. Water pH at the target gets 0.
      let table = hit.tons;
      if (num(ph) && ph >= target) table = 0;
      limeTristate[i] = table;
      limeFlag[i] = hit.flag ?? null;
      limeMethod[i] = "buffer";
      t = table;
    } else if (num(ph) && num(cec)) {
      // buildup basis: ABOVE target ⇒ 0 (never negative — no replacement, no acidification recs)
      t =
        Math.max(0, target - ph) * Math.max(0, cec) * TRI_STATE.LIME_CEC_FACTOR;
      limeMethod[i] = "water";
    }
    if (t != null) lime[i] = Math.round(clampLime(t, inp) * 10) / 10;
    const p = inp.p[i];
    // Maintenance-only is neither bound; it is an arbitrary midpoint that asserts buildup = 0 on
    // ground we cannot see.
    if (num(p)) {
      // critical precedence: user custom > Tri-State 20 ppm default
      const pCrit = pCritAt(inp);
      // Two rates, asymmetric on purpose: a deficit ppm costs the buildup rate (15, which carries a
      // buildup-efficiency factor) while a surplus ppm credits only the P₂O₅ mass actually in the
      // soil (~5.5).
      const dPpm = pCrit - p;
      let v =
        dPpm >= 0
          ? dPpm * TRI_STATE.P_LBS_P2O5_PER_PPM
          : dPpm * P_LBS_P2O5_PER_PPM_CREDIT;
      const pMaint = inp.pMaintenance?.[i];
      if (num(pMaint) && pMaint > 0) v += pMaint;
      // Subtracting it from the buildup alone would clip a credit larger than the buildup and let
      // the removal ride untouched, the bug fixed on in the total layer; folding removal in here
      // makes that special case unnecessary rather than merely correct.
      const pMin = inp.pMinCredit?.[i];
      if (num(pMin) && pMin > 0) v -= pMin;
      // THE FLOOR, on the total: excess credit is simply not spent — never a negative rate, and
      // nothing carries to another cell or season.
      v = Math.max(0, v);
      pRaw[i] = v;
      p2o5[i] = Math.round(clampP(v, inp));
    }
    const k = inp.k[i];
    // Same rule as P above: no soil test ⇒ no rate, not a maintenance-only one.
    if (num(k)) {
      let v: number;
      if (inp.k2oAlbrecht && num(cec) && cec > 0) {
        // Albrecht basis: build K to the target K-saturation (% of CEC) instead of to a critical ppm
        v = albrechtK2O(
          k,
          cec,
          num(albT.kPct) ? albT.kPct : DEFAULT_ALBRECHT_TARGETS.kPct,
        );
      } else {
        // TWO RATES, and the asymmetry is deliberate (see K_LBS_K2O_PER_PPM_CREDIT): a DEFICIT ppm
        // costs the buildup rate (8, which carries the buildup-efficiency factor), while a SURPLUS
        // ppm credits only the K₂O mass actually in the soil (~2.89).
        const kCrit = kCritAt(inp, cec);
        const dPpm = kCrit - k;
        v =
          dPpm >= 0
            ? dPpm * TRI_STATE.K_LBS_K2O_PER_PPM
            : dPpm * K_LBS_K2O_PER_PPM_CREDIT;
      }
      // Order is the point: every adjustment below describes what happens to K you SPREAD, and a
      // grower spreads the buildup and the replacement in one pass.
      const maint = inp.kMaintenance?.[i];
      if (num(maint) && maint > 0) v += maint;
      // THE FLOOR, on the total rather than on the buildup term: a surplus cell offsets its own removal
      // and once the credit exceeds it the answer is "apply nothing" — never a negative rate, which would
      // read as an instruction to remove potassium from the ground.
      v = Math.max(0, v);
      // optional clay K-fixation adjustment: divide by the cell's efficiency (≤1) so a K-fixing
      // soil gets a proportionally larger K₂O rec — applies to BOTH bases. It now covers the
      // maintenance too: clay fixes applied K regardless of why it was applied.
      const eff = inp.kFixEff?.[i];
      if (num(eff) && eff > 0) v = v / eff;
      kRaw[i] = v;
      k2o[i] = Math.round(clampK(v, inp));
    }
  }
  return { lime, p2o5, k2o, pRaw, kRaw, limeMethod, limeTristate, limeFlag };
}

// Only the lime layer starts visible — the three cover the same cells, so they read one at a time.
export type ReadoutKey =
  | "st"
  | "bph"
  | "crit"
  | "tristate"
  | "method"
  | "removal"
  | "diffusion"
  | "pmin"
  | "kfix"
  | "cation"
  | "casat"
  | "cec"
  | "kbuild"
  | "kmaint"
  | "pbuild"
  | "pmaint";

/*
 *
 * The `satisfies` below plus `_READOUT_ORDER_IS_EXHAUSTIVE` make the drift a COMPILE ERROR rather
 * than a thing to remember: add a key to `ReadoutKey` without listing it here and the build fails.
 */
export const READOUT_ORDER = [
  "st",
  "bph",
  "diffusion",
  "crit",
  "tristate",
  "method",
  "casat",
  "cec",
  "kbuild",
  "kmaint",
  "pbuild",
  "pmaint",
  "pmin",
  "kfix",
  "cation",
  "removal",
] as const satisfies readonly ReadoutKey[];

// Type-level only, so it costs nothing at runtime but fails the BUILD if a ReadoutKey is added without
// being given a display position. Exported so it is a real binding rather than dead code a linter prunes.
export type ReadoutOrderIsExhaustive =
  Exclude<ReadoutKey, (typeof READOUT_ORDER)[number]> extends never
    ? true
    : [
        "MISSING FROM READOUT_ORDER:",
        Exclude<ReadoutKey, (typeof READOUT_ORDER)[number]>,
      ];
export const READOUT_ORDER_IS_EXHAUSTIVE: ReadoutOrderIsExhaustive = true;

/*
 *
 * Assemble the hover lines a fert feature's baked readouts produce — ONE assembler shared by the
 * customize hover (MapWindow) and the Rx preset preview cards — “the preset hove should act the
 * same way”), so the two can never drift again. Emits: every baked readout in the engine's order
 * (the 'st' line keeps its long-standing “Soil test” qualifier), then the region-mean breadth line
 * for dissolved AutoRx features.
 */
export function readoutHoverLines(
  props: Record<string, unknown> | null | undefined,
): { key: string; title: string; text: string }[] {
  if (!props) return [];
  const out: { key: string; title: string; text: string }[] = [];
  for (const rk of READOUT_ORDER) {
    const text = props[`${rk}_text`];
    if (text == null) continue;
    const name = props[`${rk}_name`];
    out.push({
      key: rk,
      title:
        rk === "st" ? `Soil test ${name ?? ""}`.trim() : String(name ?? rk),
      text: String(text),
    });
  }
  const nCells = Number(props.n_cells);
  if (Number.isFinite(nCells) && nCells > 1 && out.length) {
    out.push({
      key: "ncells",
      title: "Readouts",
      text: `avg of ${nCells.toLocaleString()} cells`,
    });
  }
  return out;
}

interface Readout {
  key: ReadoutKey;
  name: string;
  arr: (number | null)[];
  unit: string;
  digits: number;
  /** When set for a cell, this string is the hover text instead of the formatted number. */
  text?: (string | null)[];
}

// The Genius breakdown readouts — per-cell factors that adjusted the rec, baked onto the feature so
// the hover shows them: change due to diffusion (corrected − raw soil test), the biology (Haney P
// mineralization) credit, and the clay K-fixation efficiency. Each is added only when its input is
// present.
function geniusReadouts(
  inputs: RecInputs,
  key: "p2o5" | "k2o",
  n = 0,
): Readout[] {
  const out: Readout[] = [];
  const raw = key === "p2o5" ? inputs.pRaw : inputs.kRaw;
  const cur = key === "p2o5" ? inputs.p : inputs.k;
  // the ADJUSTED level, not the delta: every hover shows the pair as "raw map value →
  // diffusion-adjusted value" — the number the buildup is actually solved against.
  if (raw)
    out.push({
      key: "diffusion",
      name: `Diffusion-adj. ${key === "p2o5" ? "P" : "K"}`,
      arr: cur.map((v, i) => (num(v) && num(raw[i]) ? v : null)),
      unit: "ppm",
      digits: 0,
    });
  if (key === "p2o5" && inputs.pMinCredit)
    out.push({
      key: "pmin",
      name: "Biology credit",
      arr: inputs.pMinCredit.map((c) => (num(c) && c > 0 ? c : null)),
      unit: "lbs/ac",
      digits: 0,
    });
  if (key === "k2o" && inputs.kFixEff)
    out.push({
      key: "kfix",
      name: "K-fixation eff.",
      arr: inputs.kFixEff.map((e) => (num(e) ? e * 100 : null)),
      unit: "%",
      digits: 0,
    });
  // Both carry the SAME scaling the rate itself carries, so the two lines ADD UP to the rec rather
  // than being raw ingredients the grower has to reconcile against a differently-scaled total.
  if (key === "p2o5" && inputs.pMaintenance) {
    const build = Array.from({ length: n }, (_, i) => {
      const pv = inputs.p[i];
      if (!num(pv)) return null; // no soil test ⇒ no buildup part; the maintenance line still stands
      const dPpm = pCritAt(inputs) - pv;
      const raw =
        dPpm >= 0
          ? dPpm * TRI_STATE.P_LBS_P2O5_PER_PPM
          : dPpm * P_LBS_P2O5_PER_PPM_CREDIT;
      return Math.round(raw);
    });
    const maint = inputs.pMaintenance.map((v) =>
      num(v) && v > 0 ? Math.round(v) : null,
    );
    out.push({
      key: "pbuild",
      name: "Buildup",
      arr: build,
      unit: "lbs/ac",
      digits: 0,
    });
    out.push({
      key: "pmaint",
      name: "Maintenance",
      arr: maint,
      unit: "lbs/ac",
      digits: 0,
    });
  }
  if (key === "k2o" && inputs.kMaintenance) {
    const scale = (v: number, i: number): number => {
      const eff = inputs.kFixEff?.[i];
      return num(eff) && eff > 0 ? v / eff : v;
    };
    const build = Array.from({ length: n }, (_, i) => {
      const k = inputs.k[i];
      const cec = inputs.cec[i];
      if (!num(k)) return null; // no soil test ⇒ no buildup portion (the maintenance line still stands)
      const albT = inputs.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS;
      // unfloored, matching the rec: a negative Buildup line IS the credit, and hiding it behind a 0
      // would leave the two hover lines unable to explain the rate they sum to
      const dPpm = kCritAt(inputs, cec) - k;
      const raw =
        inputs.k2oAlbrecht && num(cec) && cec > 0
          ? albrechtK2O(
              k,
              cec,
              num(albT.kPct) ? albT.kPct : DEFAULT_ALBRECHT_TARGETS.kPct,
            )
          : dPpm >= 0
            ? dPpm * TRI_STATE.K_LBS_K2O_PER_PPM
            : dPpm * K_LBS_K2O_PER_PPM_CREDIT;
      return Math.round(scale(raw, i));
    });
    const maint = inputs.kMaintenance.map((v, i) =>
      num(v) && v > 0 ? Math.round(scale(v, i)) : null,
    );
    out.push({
      key: "kbuild",
      name: "Buildup",
      arr: build,
      unit: "lbs/ac",
      digits: 0,
    });
    out.push({
      key: "kmaint",
      name: "Maintenance",
      arr: maint,
      unit: "lbs/ac",
      digits: 0,
    });
  }
  return out;
}
// One gridded per-cell value layer, carrying its per-cell hover readouts.
function buildValueLayer(
  geoms: { type: string; coordinates: unknown }[],
  values: (number | null)[],
  opts: {
    id: string;
    title: string;
    unit: string;
    defaultVisible: boolean;
    readouts?: Readout[];
  },
): ManifestLayer {
  const features = geoms.reduce<unknown[]>((acc, g, i) => {
    const v = values[i];
    if (num(v)) {
      const props: Record<string, unknown> = { value: v };
      for (const r of opts.readouts ?? []) {
        const custom = r.text?.[i];
        if (custom != null) {
          props[`${r.key}_name`] = r.name;
          props[`${r.key}_text`] = custom;
          continue;
        }
        const rv = r.arr[i];
        if (num(rv)) {
          const pow = 10 ** r.digits;
          const rounded = Math.round(rv * pow) / pow;
          props[`${r.key}_name`] = r.name;
          // Stored soil values stay Mehlich-3 at 8 inches. This is a display lens applied when a
          // number becomes text, never an input to arithmetic. Every factor is the ingest
          // conversion read backwards; a missing factor shows the stored value labelled M3.
          props[`${r.key}_text`] =
            r.unit === "ppm"
              ? `${rounded} ppm · M3 @ 8″`
              : r.key === "st" && !r.unit
                ? `${rounded} @ 8″` // pH — the depth basis still applies
                : r.unit
                  ? `${rounded} ${r.unit}`
                  : `${rounded}`;
        }
      }
      acc.push({ type: "Feature", properties: props, geometry: g });
    }
    return acc;
  }, []);
  const present = values.filter(num);
  const max = present.length ? Math.max(...present) : 0;
  return {
    id: opts.id,
    kind: "geojson",
    title: opts.title,
    inline: { type: "FeatureCollection", features } as never,
    colormap: "viridis",
    value_min: 0,
    value_max: max > 0 ? max : 1,
    unit: opts.unit,
    default_visible: opts.defaultVisible,
    default_opacity: 0.8,
    // above the covariate choropleths (55) but BELOW the boundary outline (100) — vectors
    // always paint above rasters via paintOrder, so this still sits over the ml-* COGs
    order: 60,
    content_version: "client",
    is_stale: false,
  } as ManifestLayer;
}

const REC_LAYERS = [
  // NB: the lime title is REPLACED per-run below — it names the basis and the actual target, which the Rx
  // editor makes editable. A hardcoded "pH 6.5" would lie the moment anyone changed it.
  { key: "lime", id: "fert-lime", title: "Lime rec", unit: "t/ac" },
  {
    key: "p2o5",
    id: "fert-p2o5",
    title: "P₂O₅ rec (buildup to 20 ppm)",
    unit: "lbs/ac",
  },
  {
    key: "k2o",
    id: "fert-k2o",
    title: "K₂O rec (buildup to Tri-State critical)",
    unit: "lbs/ac",
  },
] as const;

// Per-cell CRITICAL-level readout: the fixed P / CEC-tiered K critical the base rec used (or the
// user's custom fixed critical). Under the Albrecht K basis there is no critical — the readout
// instead shows the per-cell TARGET K ppm (kPct% of CEC) the rec builds toward.
function criticalReadout(
  inputs: RecInputs,
  key: "p2o5" | "k2o",
  n: number,
): Readout {
  if (key === "k2o" && inputs.k2oAlbrecht) {
    const t = inputs.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS;
    const arr = Array.from({ length: n }, (_, i) => {
      const cec = inputs.cec[i];
      return num(cec) ? (t.kPct / 100) * cec * BCSR.K_PPM_PER_MEQ : null;
    });
    return {
      key: "crit",
      name: `K target (${t.kPct}% sat)`,
      arr,
      unit: "ppm",
      digits: 0,
    };
  }
  const arr =
    key === "p2o5"
      ? Array.from({ length: n }, () => pCritAt(inputs))
      : Array.from({ length: n }, (_, i) => kCritAt(inputs, inputs.cec[i]));
  return { key: "crit", name: "Critical", arr, unit: "ppm", digits: 0 };
}
// Per-cell crop-REMOVAL readout (only when a replacement was configured). Per-nutrient replacement
// arrays (only the nutrients whose "add replacement" is on carry an array the other is null → no
// removal readout / no total).
export type ReplacementParts = {
  p2o5?: (number | null)[] | null;
  k2o?: (number | null)[] | null;
};

function removalReadout(
  repl: ReplacementParts | null | undefined,
  key: "p2o5" | "k2o",
): Readout | null {
  const arr = repl?.[key];
  return arr
    ? { key: "removal", name: "Crop removal", arr, unit: "lbs/ac", digits: 0 }
    : null;
}

function formatPhTarget(target: number): string {
  const tenth = Math.round(target * 10) / 10;
  if (Math.abs(target - tenth) < 1e-9) return String(tenth);
  return target.toFixed(2);
}

function formatTriStateTons(value: number): string {
  const tenth = Math.round(value * 10) / 10;
  const text =
    Math.abs(value - tenth) < 1e-9 ? tenth.toFixed(1) : value.toFixed(2);
  return `${text} t/ac`;
}

function bufferCell(recs: RecOutputs, index: number): boolean {
  return recs.limeMethod?.[index] === "buffer";
}

function waterPhGateNote(
  inputs: RecInputs,
  recs: RecOutputs,
  index: number,
): string | null {
  // Shown when the water-pH gate is what left this buffer cell at 0 t/ac.
  if (inputs.limeAlbrecht || recs.lime?.[index] !== 0) return null;
  const ph = inputs.ph[index];
  if (!num(ph) || ph < phTargetAt(inputs)) return null;
  return "water pH at/above target";
}

function methodTextAt(
  inputs: RecInputs,
  recs: RecOutputs,
  index: number,
): string | null {
  const method = recs.limeMethod?.[index];
  if (method === "buffer") {
    const notes: string[] = [];
    const flag = recs.limeFlag?.[index];
    if (flag) notes.push(flag);
    const gate = waterPhGateNote(inputs, recs, index);
    if (gate) notes.push(gate);
    return notes.length
      ? `Tri-State buffer · ${notes.join(" · ")}`
      : "Tri-State buffer";
  }
  // No buffer map: the hover stays pH and target pH. A blank cell on a map that
  // exists names the water pH + CEC fallback.
  if (method === "water" && inputs.bph != null && !num(inputs.bph[index])) {
    return "Water pH + CEC (no buffer pH here)";
  }
  return null;
}

function limeMethodReadouts(
  inputs: RecInputs,
  recs: RecOutputs,
  n: number,
  showPh: boolean,
): Readout[] {
  const target = phTargetAt(inputs);
  const triArr = Array.from({ length: n }, (_, i) =>
    bufferCell(recs, i) ? (recs.limeTristate?.[i] ?? null) : null,
  );
  const showTarget = (i: number) =>
    showPh ||
    recs.limeMethod?.[i] === "buffer" ||
    recs.limeMethod?.[i] === "water";
  const rows: Readout[] = [];
  if (showPh) {
    rows.push({
      key: "st",
      name: "pH",
      // Buffer-method cells keep water pH next to Buffer pH. The gate can zero
      // the rate when water pH is at or above the target, and that number has
      // to be on the hover. Albrecht passes showPh false and keeps its own line.
      arr: Array.from({ length: n }, (_, i) => inputs.ph[i] ?? null),
      unit: "",
      digits: 1,
    });
  }
  rows.push(
    {
      key: "bph",
      name: "Buffer pH",
      arr: Array.from({ length: n }, (_, i) =>
        bufferCell(recs, i) && num(inputs.bph?.[i]) ? inputs.bph![i] : null,
      ),
      unit: "",
      digits: 1,
    },
    {
      key: "cec",
      name: "CEC",
      arr: Array.from({ length: n }, (_, i) =>
        inputs.bph != null && recs.limeMethod?.[i] === "water"
          ? (inputs.cec[i] ?? null)
          : null,
      ),
      unit: "meq/100g",
      digits: 1,
    },
    {
      key: "crit",
      name: "Target pH",
      arr: Array.from({ length: n }, (_, i) => (showTarget(i) ? target : null)),
      unit: "",
      digits: 2,
      text: Array.from({ length: n }, (_, i) =>
        bufferCell(recs, i) ? target.toFixed(2) : null,
      ),
    },
    {
      key: "tristate",
      name: "Tri-State (90% lime)",
      arr: triArr,
      unit: "t/ac",
      digits: 1,
      text: triArr.map((value) =>
        num(value) ? formatTriStateTons(value) : null,
      ),
    },
    {
      key: "method",
      name: "Method",
      arr: Array.from({ length: n }, () => null),
      unit: "",
      digits: 0,
      text: Array.from({ length: n }, (_, i) => methodTextAt(inputs, recs, i)),
    },
  );
  return rows;
}

export function buildRecLayers(
  geoms: { type: string; coordinates: unknown }[],
  recs: RecOutputs,
  inputs: RecInputs,
  replacement?: ReplacementParts | null,
): ManifestLayer[] {
  const n = geoms.length;
  const readoutsFor = (key: "lime" | "p2o5" | "k2o"): Readout[] => {
    if (key === "lime") {
      // Albrecht lime targets a base-saturation profile, not pH 6.5 — show current base sat% instead
      if (inputs.limeAlbrecht) {
        const t = inputs.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS;
        // CALCIUM base saturation — since the quantity the rec ACTS ON: lime raises this to the
        // target. The old "Acidity now (target X%)" line is GONE by call: it described the
        // retired H basis, and a number labelled "target" that isn't the target is exactly what
        // this panel exists to avoid.
        const caSat = Array.from({ length: n }, (_, i) => {
          const ca = inputs.ca?.[i];
          const cec = inputs.cec[i];
          return num(ca) && num(cec) && cec > 0
            ? (ca / BCSR.CA_PPM_PER_MEQ / cec) * 100
            : null;
        });
        const base: Readout[] = [
          {
            key: "st",
            name: "pH",
            arr: Array.from({ length: n }, (_, i) =>
              bufferCell(recs, i) ? null : (inputs.ph[i] ?? null),
            ),
            unit: "",
            digits: 1,
          },
          {
            key: "casat",
            name: `Ca base sat (target ${t.caPct}%)`,
            arr: caSat,
            unit: "%",
            digits: 0,
          },
          // CEC — the denominator under the saturation figure, so it is checkable rather than trusted
          {
            key: "cec",
            name: "CEC",
            arr: inputs.cec ?? [],
            unit: "meq/100g",
            digits: 1,
          },
        ];
        // No buffer pH map: the Albrecht hover stays exactly as before (spec 11 hover rule).
        const fallback =
          inputs.bph != null &&
          (recs.limeMethod ?? []).some(
            (method) => method === "buffer" || method === "water",
          );
        return fallback
          ? [...base, ...limeMethodReadouts(inputs, recs, n, false)]
          : base;
      }
      return limeMethodReadouts(inputs, recs, n, true);
    }
    // display): st = the RAW high-res map value; the `diffusion` readout = the ADJUSTED level the
    // in the split hover — the "(moisture-adj.
    const rawArr = key === "p2o5" ? inputs.pRaw : inputs.kRaw;
    const out: Readout[] = [
      {
        key: "st",
        name: `${key === "p2o5" ? "P" : "K"}${rawArr ? " (high-res map)" : ""}`,
        arr: rawArr ?? (key === "p2o5" ? inputs.p : inputs.k) ?? [],
        unit: "ppm",
        digits: 0,
      },
      criticalReadout(inputs, key, n),
      ...geniusReadouts(inputs, key, n),
    ];
    const rem = removalReadout(replacement, key);
    if (rem) out.push(rem);
    return out;
  };
  // The lime layer's title states the basis it was actually built on. Albrecht targets a base-saturation
  // profile rather than a pH, and either target is editable, so a static string cannot describe it.
  const limeTitle = (): string => {
    const suffix = (recs.limeMethod ?? []).some((method) => method === "buffer")
      ? " · buffer pH"
      : "";
    if (inputs.limeAlbrecht) {
      const t = inputs.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS;
      return `Lime rec (Albrecht, to ${t.caPct}% Ca)${suffix}`;
    }
    const target =
      num(inputs.phTargetCustom) && inputs.phTargetCustom! > 0
        ? inputs.phTargetCustom!
        : TRI_STATE.PH_TARGET;
    return `Lime rec (approx., buildup to pH ${formatPhTarget(target)})${suffix}`;
  };
  return REC_LAYERS.map(({ key, id, title, unit }, idx) =>
    buildValueLayer(geoms, recs[key], {
      id,
      title: key === "lime" ? limeTitle() : title,
      unit,
      defaultVisible: idx === 0,
      readouts: readoutsFor(key),
    }),
  );
}

// Cation-balance layers: a standalone Mg rec, the K₂O correction (Δ) and the COMBINED K₂O total
// (base + correction) that the "+ cation balance" toggle shows.
export function buildMgLayer(
  geoms: { type: string; coordinates: unknown }[],
  cbal: CationOutputs,
  inputs: RecInputs,
): ManifestLayer {
  return buildValueLayer(geoms, cbal.mg, {
    id: "fert-mg",
    title: "Mg rec (cation balance: Ca:Mg, Mg:K)",
    unit: "lbs/ac",
    defaultVisible: false,
    readouts: [
      { key: "st", name: "Mg", arr: inputs.mg ?? [], unit: "ppm", digits: 0 },
    ],
  });
}

function totalTitle(
  nutrient: "P₂O₅" | "K₂O",
  cation: boolean,
  replacement: boolean,
  basis: "buildup" | "albrecht" = "buildup",
): string {
  const parts = [basis === "albrecht" ? "Albrecht saturation" : "buildup"];
  if (cation) parts.push("cation balance");
  if (replacement) parts.push("replacement");
  return `${nutrient} total (${parts.join(" + ")})`;
}

function windowedTotal(
  raw: (number | null)[] | undefined,
  corr: (number | null)[],
  window: RateWindow,
  n: number,
): (number | null)[] {
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const base = raw?.[i];
    const extra = corr[i];
    if (!num(base) && !num(extra)) continue;
    out[i] = Math.round(
      applyRateWindow(
        (num(base) ? base : 0) + (num(extra) ? extra : 0),
        window,
      ),
    );
  }
  return out;
}

export function assembleFertLayers(
  geoms: { type: string; coordinates: unknown }[],
  soil: RecInputs,
  opts: {
    nutrients: string[];
    cation: boolean;
    replacement?: Replacement | null;
    replP?: boolean;
    replK?: boolean;
    cationRatios?: CationRatios;
    rateWindow?: "final";
  },
): {
  layers: ManifestLayer[];
  summary: ReturnType<typeof summarizeRecs> & { cells: number };
} {
  const recs = computeRecs(soil);
  const finalWindow =
    opts.rateWindow === "final" || soil.rateWindow === "final";
  // replacement is applied PER NUTRIENT: only the nutrients whose toggle is on get their
  // crop-removal.
  const repl = opts.replacement ?? null;
  // When maintenance rides the soil, the stored replacement must never be a second addend; with it
  // excluded the -total collapses into the base rec (and is simply not built).
  const pMaintInside = (soil.pMaintenance ?? []).some((v) => num(v) && v > 0);
  const kMaintInside = (soil.kMaintenance ?? []).some((v) => num(v) && v > 0);
  const applyReplP = !!repl && (opts.replP ?? true) && !pMaintInside;
  const applyReplK = !!repl && (opts.replK ?? true) && !kMaintInside;
  const replParts: ReplacementParts = {
    p2o5: applyReplP ? repl!.p2o5 : null,
    k2o: applyReplK ? repl!.k2o : null,
  };

  const base = buildRecLayers(geoms, recs, soil, replParts).filter((l) =>
    opts.nutrients.includes(l.id.replace("fert-", "")),
  );
  base.forEach((l, i) => {
    l.default_visible = i === 0; // exactly the first SELECTED nutrient starts visible
  });
  const extra: ManifestLayer[] = [];

  const cbal = opts.cation
    ? computeCationBalance(soil, opts.cationRatios ?? DEFAULT_CATION_RATIOS)
    : null;
  if (cbal) extra.push(buildMgLayer(geoms, cbal, soil));

  // per-nutrient total = buildup + (cation K correction, K only) + (this nutrient's replacement)
  const addTotal = (
    key: "p2o5" | "k2o",
    stName: string,
    stKey: "p" | "k",
    corr: (number | null)[] | null,
    replApplied: boolean,
  ) => {
    if (!opts.nutrients.includes(key) || !corr) return;
    const stRaw = stKey === "p" ? soil.pRaw : soil.kRaw;
    const readouts: Readout[] = [
      {
        key: "st",
        name: stName,
        arr: stRaw ?? soil[stKey],
        unit: "ppm",
        digits: 0,
      },
      criticalReadout(soil, key, geoms.length),
      ...geniusReadouts(soil, key),
    ];
    if (key === "k2o" && cbal && !soil.k2oAlbrecht) {
      readouts.push({
        key: "cation",
        name: "Cation balance",
        arr: cbal.k2oCorr,
        unit: "lbs/ac",
        digits: 0,
      });
    }
    const rem = removalReadout(replParts, key);
    if (rem) readouts.push(rem);
    // The P biology (P-min) credit must offset the FULL P need — buildup-to-critical AND crop
    // removal not just the buildup that `recs.
    const pCredit =
      key === "p2o5" && (soil.pMinCredit ?? []).some((v) => num(v) && v > 0);
    let totalValues: (number | null)[];
    // NB when `soil.pMaintenance` is set the removal is already INSIDE `recs.p2o5` and this branch is not
    // reached with a `corr` — the credit-against-full-need fix it encodes has moved into `computeRecs`.
    // It stays for the à-la-carte path, which still passes removal as a separate correction array.
    if (pCredit) {
      const buildup = pBuildupRaw(soil);
      const window = pRateWindow(soil);
      totalValues = geoms.map((_, i) => {
        const b = buildup[i];
        const removal = corr[i]; // P's corr IS the crop-removal array (no cation correction for P)
        // keep a cell when EITHER the buildup or the removal is present — same as the sumArrays path this
        // supersedes, so a cell with removal but no modeled soil-test P (coverage gap) isn't silently dropped.
        if (!num(b) && !num(removal)) return null;
        const credit = soil.pMinCredit![i];
        const need = (num(b) ? b : 0) + (num(removal) ? removal : 0);
        const raw = Math.max(
          0,
          need - (num(credit) && credit > 0 ? credit : 0),
        );
        return finalWindow
          ? Math.round(applyRateWindow(raw, window))
          : Math.round(raw);
      });
    } else if (finalWindow) {
      totalValues = windowedTotal(
        key === "p2o5" ? recs.pRaw : recs.kRaw,
        corr,
        key === "p2o5" ? pRateWindow(soil) : kRateWindow(soil),
        geoms.length,
      );
    } else {
      totalValues = sumArrays(recs[key], corr);
    }
    extra.push(
      buildValueLayer(geoms, totalValues, {
        id: `fert-${key}-total`,
        title: totalTitle(
          key === "p2o5" ? "P₂O₅" : "K₂O",
          key === "k2o" && !!cbal && !soil.k2oAlbrecht, // Albrecht-K suppresses the cation K correction
          replApplied,
          key === "k2o" && soil.k2oAlbrecht ? "albrecht" : "buildup",
        ),
        unit: "lbs/ac",
        defaultVisible: false,
        readouts,
      }),
    );
  };
  addTotal("p2o5", "P", "p", replParts.p2o5 ?? null, applyReplP);
  // under the Albrecht K basis the K target is already saturation-based — do NOT also add the cation
  // balance K correction (that would double-count K). The Mg rec (buildMgLayer above) is still kept.
  let k2oCorr: (number | null)[] | null =
    cbal && opts.nutrients.includes("k2o") && !soil.k2oAlbrecht
      ? cbal.k2oCorr
      : null;
  if (applyReplK)
    k2oCorr = k2oCorr ? sumArrays(k2oCorr, replParts.k2o!) : replParts.k2o!;
  addTotal("k2o", "K", "k", k2oCorr, applyReplK);

  const layers = [...base, ...extra];

  // provenance suffixes for the K₂O / P₂O₅ layers — applied to BOTH the base rec AND its total
  // so the map/legend show what modified the rec (fixation efficiency; custom critical level)
  const kSuffix = `${(soil.kFixEff ?? []).some(num) ? " · fixation-adjusted" : ""}${num(soil.kCritCustom) ? ` · custom critical ${soil.kCritCustom} ppm` : ""}`;
  // pMinCredit uses 0 (not null) as its no-credit sentinel, so label only when SOME cell got a
  // positive credit — matching computeRecs's `pMin > 0` (else an all-zero credit falsely labels)
  const pSuffix = `${num(soil.pCritCustom) ? ` · custom critical ${soil.pCritCustom} ppm` : ""}${(soil.pMinCredit ?? []).some((v) => num(v) && v > 0) ? " · P-min credited" : ""}`;
  for (const l of layers) {
    // Albrecht basis retitle — replace the "buildup-to-critical / pH 6.5" wording with the actual
    // basis so the legend is honest (the -total already reads "Albrecht saturation" via totalTitle)
    if (l.id === "fert-k2o" && soil.k2oAlbrecht)
      l.title = l.title.replace(
        "buildup to Tri-State critical",
        "Albrecht cation saturation",
      );
    // the rec now CARRIES maintenance, so the title has to say so — a layer reading "buildup to critical"
    // while showing buildup+removal is exactly the mislabelling this file keeps having to undo
    if (
      l.id === "fert-k2o" &&
      (soil.kMaintenance ?? []).some((v) => num(v) && v > 0)
    )
      l.title = `${l.title} + maintenance`;
    // NB no lime retitles here any more: `limeTitle` in buildRecLayers bakes the real basis and
    // target (Ca% for Albrecht, the actual pH for buildup) at construction — the two substring
    // replaces that lived here matched only the OLD static title and had become silent no-ops.
    if ((l.id === "fert-k2o" || l.id === "fert-k2o-total") && kSuffix)
      l.title = `${l.title}${kSuffix}`;
    if ((l.id === "fert-p2o5" || l.id === "fert-p2o5-total") && pSuffix)
      l.title = `${l.title}${pSuffix}`;
  }

  // summary reflects only the SELECTED base nutrients (unselected recs zeroed to null)
  const pick = (key: "lime" | "p2o5" | "k2o") =>
    opts.nutrients.includes(key) ? recs[key] : recs[key].map(() => null);
  const summary = {
    ...summarizeRecs({
      lime: pick("lime"),
      p2o5: pick("p2o5"),
      k2o: pick("k2o"),
    }),
    cells: geoms.length,
  };
  return { layers, summary };
}

// Panel summary: field-average of each rec over the cells that HAVE a value, plus how
// much of the MAPPED area needs anything. `mapped` = cells with at least one rec (the
// pctNeeding denominator) — reported separately from the total cell count so the panel
// never claims a % "of the field" while some cells fell outside the modeled coverage.
export function summarizeRecs(recs: RecOutputs): {
  avgLime: number;
  avgP: number;
  avgK: number;
  pctNeeding: number;
  mapped: number;
} {
  const avg = (v: (number | null)[]) => {
    const p = v.filter(num);
    return p.length ? p.reduce((s, x) => s + x, 0) / p.length : 0;
  };
  const n = recs.lime.length;
  let needing = 0;
  let counted = 0;
  for (let i = 0; i < n; i++) {
    const vals = [recs.lime[i], recs.p2o5[i], recs.k2o[i]].filter(num);
    if (!vals.length) continue;
    counted++;
    if (vals.some((v) => v > 0)) needing++;
  }
  return {
    avgLime: Math.round(avg(recs.lime) * 10) / 10,
    avgP: Math.round(avg(recs.p2o5)),
    avgK: Math.round(avg(recs.k2o)),
    pctNeeding: counted ? Math.round((needing / counted) * 100) : 0,
    mapped: counted,
  };
}
